import dagre from 'dagre';

/**
 * Transforms hierarchical competency data into React Flow nodes and edges
 * with deduplicated action nodes
 */
export function transformToFlowData(values, direction = 'LR') {
  const nodes = [];
  const edges = [];
  const actionMap = new Map(); // key: normalized actionName, value: { data, competencyIds }

  // Track competency names for the detail panel
  const competencyNameMap = new Map();
  // Track how many actions each competency has
  const competencyActionCount = new Map();

  // Pass 1: Create Value and Competency nodes, collect actions
  values.forEach((value, vIdx) => {
    const valueId = `value-${vIdx}`;
    const totalActions = value.competencies?.reduce((sum, c) => sum + (c.actions?.length || 0), 0) || 0;

    // Value node
    nodes.push({
      id: valueId,
      type: 'valueNode',
      data: {
        name: value.valueName,
        competencyCount: value.competencies?.length || 0,
        actionCount: totalActions,
      },
      position: { x: 0, y: 0 }, // Will be set by dagre
    });

    value.competencies?.forEach((comp, cIdx) => {
      const competencyId = `competency-${vIdx}-${cIdx}`;
      competencyNameMap.set(competencyId, comp.competencyName);
      competencyActionCount.set(competencyId, comp.actions?.length || 0);

      // Collect actions for deduplication
      comp.actions?.forEach(action => {
        const key = action.actionName.toLowerCase().trim();

        if (!actionMap.has(key)) {
          actionMap.set(key, {
            actionName: action.actionName,
            entries: [...(action.entries || [])],
            entriesByCompetency: {
              [competencyId]: [...(action.entries || [])],
            },
            competencyIds: [competencyId],
          });
        } else {
          const existing = actionMap.get(key);
          existing.entries.push(...(action.entries || []));
          // Track entries by their source competency
          if (!existing.entriesByCompetency[competencyId]) {
            existing.entriesByCompetency[competencyId] = [];
          }
          existing.entriesByCompetency[competencyId].push(...(action.entries || []));
          if (!existing.competencyIds.includes(competencyId)) {
            existing.competencyIds.push(competencyId);
          }
        }
      });

      // Competency node
      nodes.push({
        id: competencyId,
        type: 'competencyNode',
        data: {
          name: comp.competencyName,
          actionCount: comp.actions?.length || 0,
          valueId: valueId,
        },
        position: { x: 0, y: 0 },
      });

      // Edge: Value -> Competency
      edges.push({
        id: `e-${valueId}-${competencyId}`,
        source: valueId,
        target: competencyId,
        type: 'smoothstep',
        animated: false,
      });
    });
  });

  // Pass 2: Create deduplicated Action nodes and edges
  let actionIdx = 0;

  actionMap.forEach((actionData) => {
    const actionId = `action-${actionIdx}`;

    // Calculate counts
    const uniqueSkills = new Set(actionData.entries.filter(e => e.skill).map(e => e.skill));
    const uniqueRoles = new Set(actionData.entries.flatMap(e => e.roles || []));

    // Get competency names for this action
    const competencyNames = actionData.competencyIds.map(id => competencyNameMap.get(id) || '');

    // Find which competencies connecting to this action have multiple actions
    const multiActionCompetencyIds = actionData.competencyIds.filter(
      compId => competencyActionCount.get(compId) > 1
    );

    // Build competency ID to name map for this action
    const competencyIdToName = {};
    actionData.competencyIds.forEach(id => {
      competencyIdToName[id] = competencyNameMap.get(id) || '';
    });

    // Action node
    nodes.push({
      id: actionId,
      type: 'actionNode',
      data: {
        name: actionData.actionName,
        skillCount: uniqueSkills.size,
        roleCount: uniqueRoles.size,
        entryCount: actionData.entries.length,
        entries: actionData.entries,
        entriesByCompetency: actionData.entriesByCompetency,
        competencyIdToName: competencyIdToName,
        competencyIds: actionData.competencyIds,
        competencyNames: competencyNames,
        isMultiParent: actionData.competencyIds.length > 1,
        // Competencies with multiple actions get separate handles
        multiActionCompetencyIds: multiActionCompetencyIds,
      },
      position: { x: 0, y: 0 },
    });

    // Edges: Competency -> Action
    actionData.competencyIds.forEach(compId => {
      const hasMultipleActions = competencyActionCount.get(compId) > 1;

      const edge = {
        id: `e-${compId}-${actionId}`,
        source: compId,
        target: actionId,
        type: 'smoothstep',
        animated: false,
      };

      // Only use targetHandle if competency has multiple actions
      if (hasMultipleActions) {
        edge.targetHandle = compId;
      }

      edges.push(edge);
    });

    actionIdx++;
  });

  // Pass 3: Apply dagre layout
  const layoutedNodes = applyDagreLayout(nodes, edges, direction);

  return { nodes: layoutedNodes, edges };
}

// Default node dimensions
const DEFAULT_NODE_DIMENSIONS = {
  valueNode: { width: 180, height: 70 },
  competencyNode: { width: 220, height: 80 },
  actionNode: { width: 200, height: 70 },
  entryNode: { width: 200, height: 90 },
};

// Layout presets
export const LAYOUT_PRESETS = {
  standard: {
    nodesep: { LR: 40, TB: 60 },
    ranksep: { LR: 120, TB: 100 },
    margin: 40,
  },
  detailed: {
    nodesep: { LR: 60, TB: 80 },
    ranksep: { LR: 150, TB: 120 },
    margin: 50,
  },
};

/**
 * Applies dagre layout algorithm to position nodes
 * @param {Array} nodes - Array of nodes
 * @param {Array} edges - Array of edges
 * @param {string} direction - 'LR' or 'TB'
 * @param {object} options - Optional config { preset: 'standard'|'detailed', nodeDimensions: {} }
 */
export function applyDagreLayout(nodes, edges, direction = 'LR', options = {}) {
  const { preset = 'standard', nodeDimensions = {} } = options;
  const layoutConfig = LAYOUT_PRESETS[preset] || LAYOUT_PRESETS.standard;
  const dims = { ...DEFAULT_NODE_DIMENSIONS, ...nodeDimensions };

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: layoutConfig.nodesep[direction] || 40,
    ranksep: layoutConfig.ranksep[direction] || 100,
    marginx: layoutConfig.margin,
    marginy: layoutConfig.margin,
  });

  // Add nodes to dagre
  nodes.forEach(node => {
    const nodeDims = dims[node.type] || { width: 180, height: 60 };
    dagreGraph.setNode(node.id, { width: nodeDims.width, height: nodeDims.height });
  });

  // Add edges to dagre
  edges.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  return nodes.map(node => {
    const dagreNode = dagreGraph.node(node.id);
    const nodeDims = dims[node.type] || { width: 180, height: 60 };

    return {
      ...node,
      position: {
        x: dagreNode.x - nodeDims.width / 2,
        y: dagreNode.y - nodeDims.height / 2,
      },
    };
  });
}
